export const M3_UNIT_12 = {
  id: "m3-u12-can-cant",
  title: "Unit 12: Can / Can't",
  topic: "Master ability and possibility with can/can't while reviewing present simple",
  iconType: "star",
  grammarImage: "/m3-u12-can-cant.jpg",
  grammarText: "Learn to express abilities, possibilities, and limitations using can/can't. Practice deducing meaning from context with native phrases like 'of course', 'at all', and 'a little'. A1-B1 level with integrated grammar review.",
  sets: [
    // ===================================================================
    // SET 1: Can Affirmative - Basic Abilities (10 preguntas)
    // 4 can + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set1",
      title: "Can: Basic Abilities",
      questions: [
        {
          type: "mc",
          q: "I ___ swim very well. I practice every day!",
          options: ["can", "am", "do"],
          a: "can",
          successtip: "✓ CAN swim = ability! 'very well' shows you have this skill. DO swim = routine (different!) 🏊"
        },
        {
          type: "mc",
          q: "She ___ speak French a little, but not fluently.",
          options: ["can", "is", "does"],
          a: "can",
          successtip: "✓ CAN speak = ability. 'a little' = partial ability. Native phrase! 🇫🇷"
        },
        {
          type: "mc",
          q: "I ___ swim every morning at 6am.",
          options: ["can", "do", "am"],
          a: "do",
          successtip: "✓ DO swim = routine/habit. 'every morning at 6am' = schedule, not ability! ⏰"
        },
        {
          type: "mc",
          q: "He ___ cook Italian food. He learned in Rome!",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN cook = ability you learned! 'He learned' = clue for CAN! 🍝"
        },
        {
          type: "mc",
          q: "She ___ a professional swimmer. She has medals.",
          options: ["can", "is", "does"],
          a: "is",
          successtip: "✓ IS a swimmer = identity/profession. CAN swim = ability. Different! 🏅"
        },
        {
          type: "mc",
          q: "They ___ play piano, but not very fast.",
          options: ["can", "do", "are"],
          a: "can",
          successtip: "✓ CAN play = ability! 'but not very fast' = you CAN do it, just slowly! 🎹"
        },
        {
          type: "mc",
          q: "My sister ___ yoga every Tuesday evening.",
          options: ["can", "does", "is"],
          a: "does",
          successtip: "✓ DOES yoga = routine. 'every Tuesday' = schedule! CAN = ability to do it. 🧘"
        },
        {
          type: "mc",
          q: "I ___ a good cook, but I don't like restaurants.",
          options: ["can", "am", "do"],
          a: "am",
          successtip: "✓ AM a good cook = description/state. CAN cook = ability. Context matters! 👨‍🍳"
        },
        {
          type: "mc",
          q: "We ___ drive, but we don't have a car.",
          options: ["can", "do", "are"],
          a: "can",
          successtip: "✓ CAN drive = ability/skill. 'but we don't have a car' = you know HOW! 🚗"
        },
        {
          type: "mc",
          q: "My parents ___ teachers. They work at my school.",
          options: ["can", "are", "do"],
          a: "are",
          successtip: "✓ ARE teachers = profession/identity. 'They work at' confirms it! 👨‍🏫"
        }
      ]
    },

    // ===================================================================
    // SET 2: Can't Negative - Limitations (10 preguntas)
    // 4 can't + 3 don't/doesn't + 3 isn't/aren't
    // ===================================================================
    {
      id: "m3-u12-set2",
      title: "Can't: Limitations",
      questions: [
        {
          type: "mc",
          q: "I ___ speak Chinese at all. I never study it.",
          options: ["can't", "don't", "am not"],
          a: "can't",
          successtip: "✓ CAN'T speak = zero ability! 'at all' = native phrase meaning 'zero'! 🚫"
        },
        {
          type: "mc",
          q: "She ___ drive at all. She's too young for a license.",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T drive = no ability/permission. 'too young' = reason why! 🚗❌"
        },
        {
          type: "mc",
          q: "He ___ eat meat. He's a vegetarian.",
          options: ["can't", "doesn't", "isn't"],
          a: "doesn't",
          successtip: "✓ DOESN'T eat = choice/habit. 'He's a vegetarian' = reason. CAN'T = physically impossible! 🥗"
        },
        {
          type: "mc",
          q: "I ___ swim at all. I'm scared of water!",
          options: ["can't", "don't", "am not"],
          a: "can't",
          successtip: "✓ CAN'T swim = no ability! 'at all' + 'scared' = zero skill! 🏊❌"
        },
        {
          type: "mc",
          q: "They ___ professional athletes. They just play for fun.",
          options: ["can't", "aren't", "don't"],
          a: "aren't",
          successtip: "✓ AREN'T athletes = not their identity. 'just for fun' = clue! ⚽"
        },
        {
          type: "mc",
          q: "My brother ___ play guitar at all. He never practices.",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T play = no ability. 'never practices' = reason for no skill! 🎸"
        },
        {
          type: "mc",
          q: "She ___ work on weekends. She stays home with family.",
          options: ["can't", "doesn't", "isn't"],
          a: "doesn't",
          successtip: "✓ DOESN'T work = routine/choice. 'stays home' = what she does instead! 🏡"
        },
        {
          type: "mc",
          q: "We ___ very good at math, but we try hard!",
          options: ["can't", "aren't", "don't"],
          a: "aren't",
          successtip: "✓ AREN'T very good = description/state. 'but we try' = you still do it! 📐"
        },
        {
          type: "mc",
          q: "He ___ dance at all. He has two left feet!",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T dance = no ability! 'two left feet' = slang for clumsy! Native expression! 💃❌"
        },
        {
          type: "mc",
          q: "I ___ a morning person. I hate waking up early!",
          options: ["can't", "am not", "don't"],
          a: "am not",
          successtip: "✓ AM NOT a morning person = description/personality. Native phrase! 'hate waking up' = clue! 🌅"
        }
      ]
    },

    // ===================================================================
    // SET 3: Can Questions + Context Clues (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set3",
      title: "Can Questions",
      questions: [
        {
          type: "mc",
          q: "___ you speak English? Of course! I'm speaking now!",
          options: ["Can", "Do", "Are"],
          a: "Can",
          successtip: "✓ CAN you speak = asking about ability! 'Of course!' = native phrase meaning 'obviously yes'! 💬"
        },
        {
          type: "mc",
          q: "___ she play tennis? Yes, very well actually!",
          options: ["Can", "Does", "Is"],
          a: "Can",
          successtip: "✓ CAN she play = ability question. 'very well' confirms skill! 🎾"
        },
        {
          type: "mc",
          q: "___ you work on Saturdays? No, I rest on weekends.",
          options: ["Can", "Do", "Are"],
          a: "Do",
          successtip: "✓ DO you work = asking about routine/schedule. 'on Saturdays' = time clue! 📅"
        },
        {
          type: "mc",
          q: "___ they swim? Not at all. They never learned.",
          options: ["Can", "Do", "Are"],
          a: "Can",
          successtip: "✓ CAN they swim = ability. 'Not at all' = zero ability! Native phrase! 🏊❌"
        },
        {
          type: "mc",
          q: "___ he a doctor? Yes, he works at the hospital.",
          options: ["Can", "Does", "Is"],
          a: "Is",
          successtip: "✓ IS he a doctor = profession/identity. 'works at hospital' confirms it! 👨‍⚕️"
        },
        {
          type: "mc",
          q: "___ you cook? A little, but I'm not very good.",
          options: ["Can", "Do", "Are"],
          a: "Can",
          successtip: "✓ CAN you cook = ability. 'A little' = partial skill! Native phrase! 🍳"
        },
        {
          type: "mc",
          q: "___ she eat breakfast every day? Yes, at 7am.",
          options: ["Can", "Does", "Is"],
          a: "Does",
          successtip: "✓ DOES she eat = routine question. 'every day at 7am' = schedule! ☕"
        },
        {
          type: "mc",
          q: "___ you tired? Yes, I need to sleep!",
          options: ["Can", "Do", "Are"],
          a: "Are",
          successtip: "✓ ARE you tired = state/feeling. 'need to sleep' = clue for ARE! 😴"
        },
        {
          type: "mc",
          q: "___ your brother drive? No, he can't. He's only 14.",
          options: ["Can", "Does", "Is"],
          a: "Can",
          successtip: "✓ CAN he drive = ability/permission. 'He's only 14' = too young! 🚗"
        },
        {
          type: "mc",
          q: "___ they students? Yes, they go to university.",
          options: ["Can", "Do", "Are"],
          a: "Are",
          successtip: "✓ ARE they students = identity. 'go to university' confirms it! 🎓"
        }
      ]
    },

    // ===================================================================
    // SET 4: Context Deduction - Native Phrases (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set4",
      title: "Context Clues",
      questions: [
        {
          type: "mc",
          q: "I ___ read in French, but not very fast.",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN read = ability! 'but not very fast' = you CAN do it, just slowly! 📖"
        },
        {
          type: "mc",
          q: "She ___ understand Spanish at all. She never studies it.",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T understand = zero ability. 'at all' = native phrase! 'never studies' = reason! 🇪🇸❌"
        },
        {
          type: "mc",
          q: "My dad ___ coffee every morning. It's his routine.",
          options: ["can", "drinks", "is"],
          a: "drinks",
          successtip: "✓ DRINKS coffee = habit! 'every morning' + 'routine' = clear schedule clue! ☕"
        },
        {
          type: "mc",
          q: "I ___ run very well. I practice with my team!",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN run = ability/skill. 'very well' + 'practice' = trained skill! 🏃"
        },
        {
          type: "mc",
          q: "He ___ a fast runner, but he doesn't compete.",
          options: ["can", "is", "does"],
          a: "is",
          successtip: "✓ IS a fast runner = description. 'but doesn't compete' = choice, not ability! 🏃‍♂️"
        },
        {
          type: "mc",
          q: "They ___ play soccer at all. They prefer basketball.",
          options: ["can't", "don't", "aren't"],
          a: "don't",
          successtip: "✓ DON'T play = choice/habit. 'prefer basketball' = they choose not to! CAN'T = unable! ⚽"
        },
        {
          type: "mc",
          q: "My sister ___ sing beautifully. She's in a choir!",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN sing = ability! 'beautifully' + 'in a choir' = skill! 🎤"
        },
        {
          type: "mc",
          q: "We ___ work from home on Fridays. It's company policy.",
          options: ["can", "do", "are"],
          a: "do",
          successtip: "✓ DO work = routine. 'on Fridays' + 'company policy' = schedule! 💼"
        },
        {
          type: "mc",
          q: "I ___ speak Japanese a little. I lived in Tokyo!",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN speak = ability! 'a little' = partial skill. 'lived in Tokyo' = how you learned! 🇯🇵"
        },
        {
          type: "mc",
          q: "You ___ very tall! Do you play basketball?",
          options: ["can", "do", "are"],
          a: "are",
          successtip: "✓ ARE very tall = physical description. CAN = ability, not height! 🏀"
        }
      ]
    },

    // ===================================================================
    // SET 5: Connectors with Can (but, and, because, so) (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set5",
      title: "Connectors with Can",
      questions: [
        {
          type: "mc",
          q: "I ___ swim, but I can't dive.",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN swim BUT can't dive = contrast in abilities! Connector 'but' shows difference! 🏊"
        },
        {
          type: "mc",
          q: "She ___ cook and clean very well.",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN cook AND clean = multiple abilities! Connector 'and' adds skills! 🧹"
        },
        {
          type: "mc",
          q: "He ___ drive because he doesn't have a license.",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T drive BECAUSE no license = reason for inability! Connector 'because' explains! 🚗❌"
        },
        {
          type: "mc",
          q: "I ___ tired, so I can't work tonight.",
          options: ["can", "do", "am"],
          a: "am",
          successtip: "✓ AM tired SO can't work = state causing result! Connector 'so' shows consequence! 😴"
        },
        {
          type: "mc",
          q: "They ___ speak French and English fluently.",
          options: ["can", "do", "are"],
          a: "can",
          successtip: "✓ CAN speak French AND English = multiple abilities! 'fluently' shows skill level! 🌍"
        },
        {
          type: "mc",
          q: "My brother ___ play guitar, but he doesn't practice.",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN play BUT doesn't practice = has ability but chooses not to use it! 🎸"
        },
        {
          type: "mc",
          q: "She ___ eat lunch at 1pm every day because of work.",
          options: ["can", "does", "is"],
          a: "does",
          successtip: "✓ DOES eat = routine! 'every day at 1pm' = schedule. 'because of work' = reason! 🍽️"
        },
        {
          type: "mc",
          q: "We ___ very busy, so we can't meet today.",
          options: ["can", "do", "are"],
          a: "are",
          successtip: "✓ ARE busy SO can't meet = state causing result! Connector 'so' explains why! 📅"
        },
        {
          type: "mc",
          q: "I ___ dance a little, but I'm not very good.",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN dance a little BUT not very good = limited ability! 'but' shows limitation! 💃"
        },
        {
          type: "mc",
          q: "He ___ walk to work because he lives nearby.",
          options: ["can", "does", "is"],
          a: "does",
          successtip: "✓ DOES walk = routine/habit! 'because he lives nearby' = reason for the routine! 🚶"
        }
      ]
    },

    // ===================================================================
    // SET 6: Recycled Vocabulary - Family & Work (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set6",
      title: "Family & Work Abilities",
      questions: [
        {
          type: "mc",
          q: "My mom ___ cook amazing Italian food!",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN cook = ability/skill! 'amazing' shows high level! Mom's skills! 👩‍🍳"
        },
        {
          type: "mc",
          q: "My sister ___ a nurse. She works at the hospital.",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS a nurse = profession. 'works at hospital' confirms identity! 👩‍⚕️"
        },
        {
          type: "mc",
          q: "I ___ help my dad at all. I'm too young.",
          options: ["can't", "don't", "am not"],
          a: "can't",
          successtip: "✓ CAN'T help = no ability. 'at all' + 'too young' = clear limitation! 🚫"
        },
        {
          type: "mc",
          q: "My brother ___ homework every evening at 6pm.",
          options: ["can", "does", "is"],
          a: "does",
          successtip: "✓ DOES homework = routine! 'every evening at 6pm' = schedule! 📚"
        },
        {
          type: "mc",
          q: "My grandparents ___ use smartphones very well!",
          options: ["can", "do", "are"],
          a: "can",
          successtip: "✓ CAN use = ability! 'very well' = they learned the skill! Tech-savvy grandparents! 📱"
        },
        {
          type: "mc",
          q: "My aunt ___ speak three languages fluently.",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN speak = ability! 'three languages' + 'fluently' = impressive skill! 🌍"
        },
        {
          type: "mc",
          q: "My uncle ___ a teacher. He teaches math.",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS a teacher = profession. 'teaches math' confirms it! 👨‍🏫"
        },
        {
          type: "mc",
          q: "My cousin ___ drive at all. She failed the test.",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T drive = no ability. 'at all' + 'failed the test' = reason! 🚗❌"
        },
        {
          type: "mc",
          q: "I ___ work with my father on weekends.",
          options: ["can", "do", "am"],
          a: "do",
          successtip: "✓ DO work = routine/habit. 'on weekends' = schedule clue! 👔"
        },
        {
          type: "mc",
          q: "My parents ___ very strict, but they're kind.",
          options: ["can't", "aren't", "don't"],
          a: "aren't",
          successtip: "✓ AREN'T very strict = description. 'but they're kind' = contrast! 👨‍👩‍👧"
        }
      ]
    },

    // ===================================================================
    // SET 7: Real-Life Situations (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set7",
      title: "Real-Life Situations",
      questions: [
        {
          type: "mc",
          q: "I ___ see the board at all. I need glasses!",
          options: ["can't", "don't", "am not"],
          a: "can't",
          successtip: "✓ CAN'T see = physical inability. 'at all' + 'need glasses' = clear problem! 👓"
        },
        {
          type: "mc",
          q: "She ___ hear you. Can you speak louder?",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T hear = inability right now! 'speak louder' = solution! 👂"
        },
        {
          type: "mc",
          q: "I ___ coffee in the morning. It wakes me up!",
          options: ["can", "drink", "am"],
          a: "drink",
          successtip: "✓ DRINK coffee = habit! 'in the morning' = routine. 'wakes me up' = reason! ☕"
        },
        {
          type: "mc",
          q: "We ___ find the restaurant. Do you have the address?",
          options: ["can't", "don't", "aren't"],
          a: "can't",
          successtip: "✓ CAN'T find = unable right now! 'Do you have address?' = asking for help! 🗺️"
        },
        {
          type: "mc",
          q: "He ___ very hungry. He didn't eat breakfast!",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS very hungry = current state. 'didn't eat breakfast' = reason! 🍔"
        },
        {
          type: "mc",
          q: "I ___ lift this box. It's too heavy!",
          options: ["can't", "don't", "am not"],
          a: "can't",
          successtip: "✓ CAN'T lift = physical limitation. 'too heavy' = reason for inability! 📦"
        },
        {
          type: "mc",
          q: "They ___ to the gym three times a week.",
          options: ["can", "go", "are"],
          a: "go",
          successtip: "✓ GO to gym = routine! 'three times a week' = frequency/schedule! 🏋️"
        },
        {
          type: "mc",
          q: "She ___ remember his name at all. She just met him!",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T remember = inability. 'at all' + 'just met him' = too new! 🤔"
        },
        {
          type: "mc",
          q: "I ___ very cold. Can you close the window?",
          options: ["can", "do", "am"],
          a: "am",
          successtip: "✓ AM very cold = current state/feeling. 'close window' = solution! ❄️"
        },
        {
          type: "mc",
          q: "We ___ understand the instructions at all. They're confusing!",
          options: ["can't", "don't", "aren't"],
          a: "can't",
          successtip: "✓ CAN'T understand = inability. 'at all' + 'confusing' = too difficult! 📋"
        }
      ]
    },

    // ===================================================================
    // SET 8: Sports & Hobbies (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set8",
      title: "Sports & Hobbies",
      questions: [
        {
          type: "mc",
          q: "I ___ play tennis, but I'm not very good.",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN play = ability! 'but not very good' = limited skill, still CAN! 🎾"
        },
        {
          type: "mc",
          q: "She ___ yoga every morning before work.",
          options: ["can", "does", "is"],
          a: "does",
          successtip: "✓ DOES yoga = routine! 'every morning before work' = schedule! 🧘"
        },
        {
          type: "mc",
          q: "He ___ a professional athlete. He trains 6 hours daily!",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS a professional athlete = identity/profession. 'trains 6 hours' = proof! 🏃‍♂️"
        },
        {
          type: "mc",
          q: "We ___ skate at all. We never learned.",
          options: ["can't", "don't", "aren't"],
          a: "can't",
          successtip: "✓ CAN'T skate = zero ability. 'at all' + 'never learned' = no skill! ⛸️"
        },
        {
          type: "mc",
          q: "I ___ run very fast. I practice every day!",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN run = ability! 'very fast' + 'practice every day' = trained skill! 🏃"
        },
        {
          type: "mc",
          q: "My brother ___ basketball on Saturdays with his team.",
          options: ["can", "plays", "is"],
          a: "plays",
          successtip: "✓ PLAYS basketball = routine/activity! 'on Saturdays' = schedule! 🏀"
        },
        {
          type: "mc",
          q: "They ___ swim at all. They're scared of water!",
          options: ["can't", "don't", "aren't"],
          a: "can't",
          successtip: "✓ CAN'T swim = no ability. 'at all' + 'scared' = reason for inability! 🏊❌"
        },
        {
          type: "mc",
          q: "She ___ very good at chess. She wins tournaments!",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS very good = description of skill level. 'wins tournaments' = proof! ♟️"
        },
        {
          type: "mc",
          q: "I ___ dance a little, but I need more practice.",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN dance = ability! 'a little' = partial skill. 'need practice' = room to improve! 💃"
        },
        {
          type: "mc",
          q: "We ___ hiking every Sunday in the mountains.",
          options: ["can", "go", "are"],
          a: "go",
          successtip: "✓ GO hiking = routine! 'every Sunday' = regular schedule! ⛰️"
        }
      ]
    },

    // ===================================================================
    // SET 9: Daily Life & Routines (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set9",
      title: "Daily Life",
      questions: [
        {
          type: "mc",
          q: "I ___ wake up early. I'm a morning person!",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN wake up early = ability/no problem! 'morning person' = native phrase! 🌅"
        },
        {
          type: "mc",
          q: "She ___ breakfast at 7am every day.",
          options: ["can", "eats", "is"],
          a: "eats",
          successtip: "✓ EATS breakfast = routine! 'at 7am every day' = schedule! 🍳"
        },
        {
          type: "mc",
          q: "He ___ sleep at all. The noise is too loud!",
          options: ["can't", "doesn't", "isn't"],
          a: "can't",
          successtip: "✓ CAN'T sleep = unable right now! 'at all' + 'too loud' = reason! 😴❌"
        },
        {
          type: "mc",
          q: "I ___ very tired. I need to rest.",
          options: ["can", "do", "am"],
          a: "am",
          successtip: "✓ AM very tired = current state. 'need to rest' = consequence! 😫"
        },
        {
          type: "mc",
          q: "We ___ work from home on Fridays.",
          options: ["can", "do", "are"],
          a: "can",
          successtip: "✓ CAN work from home = permission/ability! 'on Fridays' = when it's possible! 🏡"
        },
        {
          type: "mc",
          q: "My sister ___ her homework after dinner every night.",
          options: ["can", "does", "is"],
          a: "does",
          successtip: "✓ DOES homework = routine! 'after dinner every night' = schedule! 📚"
        },
        {
          type: "mc",
          q: "I ___ concentrate at all. It's too noisy here!",
          options: ["can't", "don't", "am not"],
          a: "can't",
          successtip: "✓ CAN'T concentrate = unable right now. 'at all' + 'too noisy' = reason! 🔇"
        },
        {
          type: "mc",
          q: "They ___ very organized. Their house is always clean!",
          options: ["can", "do", "are"],
          a: "are",
          successtip: "✓ ARE very organized = personality/description. 'house always clean' = proof! 🏠"
        },
        {
          type: "mc",
          q: "He ___ cook very well. He's a chef!",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN cook = ability! 'very well' + 'He's a chef' = professional skill! 👨‍🍳"
        },
        {
          type: "mc",
          q: "I ___ to bed at 10pm on weekdays.",
          options: ["can", "go", "am"],
          a: "go",
          successtip: "✓ GO to bed = routine! 'at 10pm on weekdays' = regular schedule! 🛏️"
        }
      ]
    },

    // ===================================================================
    // SET 10: Mixed Practice - Integration (10 preguntas)
    // 4 can/can't + 3 do/does + 3 am/is/are
    // ===================================================================
    {
      id: "m3-u12-set10",
      title: "Mixed Practice",
      questions: [
        {
          type: "mc",
          q: "I ___ speak German a little. I lived in Berlin!",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN speak = ability! 'a little' + 'lived in Berlin' = how you learned! 🇩🇪"
        },
        {
          type: "mc",
          q: "She ___ very smart. She gets good grades!",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS very smart = description. 'gets good grades' = evidence! 🎓"
        },
        {
          type: "mc",
          q: "We ___ eat meat. We're vegetarians.",
          options: ["can't", "don't", "aren't"],
          a: "don't",
          successtip: "✓ DON'T eat = choice/habit! 'We're vegetarians' = lifestyle choice, not inability! 🥗"
        },
        {
          type: "mc",
          q: "He ___ play guitar very well. He takes lessons!",
          options: ["can", "does", "is"],
          a: "can",
          successtip: "✓ CAN play = ability! 'very well' + 'takes lessons' = improving skill! 🎸"
        },
        {
          type: "mc",
          q: "I ___ exercise every morning at the gym.",
          options: ["can", "do", "am"],
          a: "do",
          successtip: "✓ DO exercise = routine! 'every morning at the gym' = schedule! 🏋️"
        },
        {
          type: "mc",
          q: "They ___ understand English at all. They only speak Spanish.",
          options: ["can't", "don't", "aren't"],
          a: "can't",
          successtip: "✓ CAN'T understand = zero ability. 'at all' + 'only speak Spanish' = no English skill! 🗣️"
        },
        {
          type: "mc",
          q: "My mom ___ a doctor, but she works at home now.",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS a doctor = profession stays the same. 'but works at home' = location change! 👩‍⚕️"
        },
        {
          type: "mc",
          q: "I ___ remember names very well. I have a good memory!",
          options: ["can", "do", "am"],
          a: "can",
          successtip: "✓ CAN remember = ability! 'very well' + 'good memory' = natural skill! 🧠"
        },
        {
          type: "mc",
          q: "She ___ coffee every day. She loves it!",
          options: ["can", "drinks", "is"],
          a: "drinks",
          successtip: "✓ DRINKS coffee = habit! 'every day' + 'loves it' = routine! ☕"
        },
        {
          type: "mc",
          q: "We ___ very happy with our new house!",
          options: ["can", "do", "are"],
          a: "are",
          successtip: "✓ ARE very happy = state/feeling. 'with our new house' = reason! 🏡😊"
        }
      ]
    }
    ],

    readingSet: {
  id: "m3-u12-reading",
  title: "Reading: Life, Skills, and Routines",
  category: "reading",

  questions: [
 {
      type: "passage",
      passage: `**Tom's Daily Study**

Hi! I'm Tom. I can speak three languages: English, Spanish, and a little French. I study languages every day to improve my skills. I want to travel to Europe next year!`
    },
    {
      type: "mc",
      q: "How many languages can Tom speak?",
      options: ["Two", "Three", "Four"],
      a: "Three",
      successtip: "✓ POLYGLOT!\nTom can speak English, Spanish, and a little French. 🌍"
    },
    {
      type: "mc",
      q: "What does Tom do every day?",
      options: ["He studies languages", "He plays sports", "He works"],
      a: "He studies languages",
      successtip: "✓ CONSISTENCY!\nHe studies every day as part of his routine. 📚"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Sarah the Teacher (preguntas 3-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Sarah's Evenings**

My name is Sarah. I'm a teacher. I can't sing at all, but I can play piano very well. I practice every evening in my living room. It's my favorite way to relax after school.`
    },
    {
      type: "mc",
      q: "What is Sarah's profession?",
      options: ["Singer", "Teacher", "Pianist"],
      a: "Teacher",
      successtip: "✓ PROFESSION!\nSarah 'is' (am/is/are) a teacher. That is her job. 👩‍🏫"
    },
    {
      type: "mc",
      q: "What can't Sarah do?",
      options: ["Sing", "Play piano", "Teach"],
      a: "Sing",
      successtip: "✓ NO ABILITY!\nShe says she 'can't sing at all.' 🎤❌"
    },
    {
      type: "mc",
      q: "When does Sarah practice piano?",
      options: ["Every morning", "Every evening", "On weekends"],
      a: "Every evening",
      successtip: "✓ EVENING ROUTINE!\nShe plays the piano every night to relax. 🎹🌙"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Mike the Athlete (preguntas 6-8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Mike's Training**

My brother Mike is very athletic. He can run very fast and swim very well. He's a professional athlete. He trains six days a week at the national sports center.`
    },
    {
      type: "mc",
      q: "What can Mike do very well?",
      options: ["Run and swim", "Cook and clean", "Sing and dance"],
      a: "Run and swim",
      successtip: "✓ ATHLETIC!\nMike has strong physical abilities in water and on land. 🏊🏃"
    },
    {
      type: "mc",
      q: "What is Mike's profession?",
      options: ["Teacher", "Professional athlete", "Chef"],
      a: "Professional athlete",
      successtip: "✓ CAREER!\nHis job is to compete and train in sports. 🏅"
    },
    {
      type: "mc",
      q: "How often does Mike train?",
      options: ["Every day", "Six days a week", "On weekends"],
      a: "Six days a week",
      successtip: "✓ HARD WORK!\nHe trains (do/does) six days a week to stay professional. 💪"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Anna's Commute (preguntas 9-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Anna's Bicycle**

I'm Anna. I can't drive because I'm only 15 years old. But I can ride a bicycle very well! I ride to school every day. It's faster than walking.`
    },
    {
      type: "mc",
      q: "Why can't Anna drive?",
      options: ["She's too young", "She's scared", "She doesn't want to"],
      a: "She's too young",
      successtip: "✓ AGE LIMIT!\nAt 15, she is not old enough for a driver's license. 🚗❌"
    },
    {
      type: "mc",
      q: "How does Anna go to school?",
      options: ["By car", "By bicycle", "By bus"],
      a: "By bicycle",
      successtip: "✓ TWO WHEELS!\nShe rides her bike to school every day. 🚲"
    }
  ]
},
  reviewset:  {
      id: "m3-u12-review",
      title: "Unit 12 Review",
      questions: [
        {
          type: "match",
          q: "Final Review: Match abilities to context",
          pairs: [
            { left: "I can swim", right: "I have this ability" },
            { left: "I do swim", right: "I swim regularly (routine)" },
            { left: "I am a swimmer", right: "It's my identity/profession" }
          ],
          a: "completed",
          successtip: "✓ CAN = ability | DO = routine | AM = identity! Three different meanings! 🏊"
        },
        {
          type: "mc",
          q: "___ you speak English? Of course!",
          options: ["Can", "Do", "Are"],
          a: "Can",
          successtip: "✓ CAN you speak = asking about ability! 'Of course!' = obviously yes! 💬"
        },
        {
          type: "mc",
          q: "She ___ very tired. She needs to sleep.",
          options: ["can", "does", "is"],
          a: "is",
          successtip: "✓ IS very tired = current state. 'needs to sleep' = consequence! 😴"
        },
        {
          type: "input",
          q: "I ___ understand French at all. (zero ability)",
          a: "can't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CAN'T = zero ability! 'at all' = native phrase meaning 'zero'! 🚫"
        },
        {
          type: "mc",
          q: "He ___ coffee every morning. It's his routine.",
          options: ["can", "drinks", "is"],
          a: "drinks",
          successtip: "✓ DRINKS = habit! 'every morning' = routine, not ability! ☕"
        },
        {
          type: "order",
          q: "Order the words: can / I / a little / cook",
          words: ["I", "can", "cook", "a", "little."],
          a: "I can cook a little.",
          successtip: "✓ I CAN cook a little = partial ability! 'a little' = native phrase! 👨‍🍳"
        },
        {
          type: "mc",
          q: "They ___ work from home because of their job.",
          options: ["can", "do", "are"],
          a: "can",
          successtip: "✓ CAN work from home = permission/ability! 'because of job' = reason! 🏡"
        },
        {
          type: "match",
          q: "Match the native phrases",
          pairs: [
            { left: "Of course", right: "Obviously yes!" },
            { left: "At all", right: "Zero/nothing" },
            { left: "A little", right: "Partial ability" }
          ],
          a: "completed",
          successtip: "✓ Native phrases mastered! Use them like a native speaker! 🗣️"
        },
        {
          type: "mc",
          q: "I ___ a morning person. I hate waking up early!",
          options: ["can't", "am not", "don't"],
          a: "am not",
          successtip: "✓ AM NOT a morning person = personality/description! Native phrase! 🌅❌"
        },
        {
          type: "mc",
          q: "She ___ swim, but she ___ dive.",
          options: ["can / can't", "does / doesn't", "is / isn't"],
          a: "can / can't",
          successtip: "✓ CAN swim BUT can't dive = contrast in abilities! You completed Unit 12! 🎉🏊"
        }
      ]
    }
};
